import React, { useState } from 'react';
import axios from 'axios';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API, imgSrc, adminHeaders } from '../../lib/shop';

const empty = { name: '', description: '', price: '', image_url: '', category: 'Cleaning Products', stock: '', active: true, sort_order: 0 };

export const ProductForm = ({ product, onSaved, onCancel }) => {
  const [form, setForm] = useState(product ? { ...product, stock: product.stock ?? '', price: product.price } : empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await axios.post(`${API}/shop/admin/upload-image`, data, { headers: adminHeaders() });
      setForm(f => ({ ...f, image_url: res.data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, price: parseFloat(form.price), image_url: form.image_url,
      category: form.category || 'General', stock: form.stock === '' ? null : parseInt(form.stock, 10),
      active: !!form.active, sort_order: parseInt(form.sort_order || 0, 10),
    };
    try {
      const res = product
        ? await axios.put(`${API}/shop/admin/products/${product.id}`, payload, { headers: adminHeaders() })
        : await axios.post(`${API}/shop/admin/products`, payload, { headers: adminHeaders() });
      toast.success(product ? 'Product updated' : 'Product created');
      onSaved(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save product. Check all fields.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4" data-testid="product-form">
      <h3 className="font-bold text-lg text-gray-900">{product ? 'Edit product' : 'New product'}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2"><span className="text-sm font-medium text-gray-700">Name *</span><Input name="name" value={form.name} onChange={set} required data-testid="product-form-name" /></label>
        <label className="block sm:col-span-2"><span className="text-sm font-medium text-gray-700">Description</span><Textarea name="description" value={form.description} onChange={set} rows={4} data-testid="product-form-description" /></label>
        <label className="block"><span className="text-sm font-medium text-gray-700">Price (£) *</span><Input type="number" step="0.01" min="0.30" name="price" value={form.price} onChange={set} required data-testid="product-form-price" /></label>
        <label className="block"><span className="text-sm font-medium text-gray-700">Category</span><Input name="category" value={form.category} onChange={set} data-testid="product-form-category" /></label>
        <label className="block"><span className="text-sm font-medium text-gray-700">Stock (leave blank = unlimited)</span><Input type="number" min="0" name="stock" value={form.stock} onChange={set} data-testid="product-form-stock" /></label>
        <label className="block"><span className="text-sm font-medium text-gray-700">Sort order</span><Input type="number" name="sort_order" value={form.sort_order} onChange={set} data-testid="product-form-sort" /></label>
        <div className="sm:col-span-2">
          <span className="text-sm font-medium text-gray-700 block mb-1">Image</span>
          <div className="flex flex-wrap items-center gap-4">
            <img src={imgSrc(form.image_url)} alt="" className="w-20 h-20 rounded-lg object-cover bg-pink-50 border" />
            <div className="flex-1 min-w-[200px] space-y-2">
              <Input name="image_url" value={form.image_url} onChange={set} placeholder="Image URL or upload below" data-testid="product-form-image-url" />
              <label className="inline-flex items-center gap-2 text-sm text-pink-600 cursor-pointer hover:underline">
                <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload image'}
                <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="product-form-image-file" />
              </label>
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" name="active" checked={!!form.active} onChange={set} data-testid="product-form-active" /> Visible in shop</label>
      </div>
      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="product-form-cancel">Cancel</Button>
        <Button type="submit" disabled={saving || uploading} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="product-form-save">{saving ? 'Saving...' : 'Save product'}</Button>
      </div>
    </form>
  );
};
